import React, { useEffect, useState, useMemo } from "react";
import { EditRuleProps } from "../lead-assignment-props";
import LeftPanel from "./create-rule-leftPanel";
import { Formik } from "formik";
import * as Yup from "yup";
import ListOfPartnersBasedOnRule from "./list-partners-based-on-rule";
import { routes } from "../../../../constants/routes";
import { Partner } from "./partner";
import { useNavigate } from "react-router-dom";
import { editRule, getRuleById } from "../../../../api/lead-assignment";
import { AlertComponent } from "../../../../common/components";
import { useSetLoading } from "../../../../common/hooks/appearance";

const EditRule: React.FC<EditRuleProps> = ({ currentRule, setIsEdit, fetchRules,viewRule=false }) => {
    const navigate = useNavigate();
    const [existingAddedPartners, setExistingAddedPartners] = useState<Partner[]>([]);
    const [addedPartners, setAddedPartners] = useState<Partner[]>([]);
    const [matchedPartners, setMatchedPartners] = useState<Partner[]>([]);
    const [alertMessage, setAlertMessage] = useState<string | null>(null);
    const [alertType, setAlertType] = useState<
        "error" | "success" | "info" | "warning"
    >("info");
    const [alertOpen, setAlertOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState(false)

    const getRuleAPI = async () => {
        setLoading(true);
        try {
            const response = await getRuleById(currentRule?.id, true);
            if (response.data.success) {
                const rawData = response?.data?.data?.assignmentRule?.LeadAssignmentRulePartnerMapping || [];
                const flattenedPartners = rawData?.map((item: { partner: any }) => item?.partner);
                setAddedPartners(flattenedPartners);
                setExistingAddedPartners(flattenedPartners);
                setMatchedPartners(response?.data?.data?.matchedPartners)
            }
        } catch (error: any) {
            setAlertMessage(
                error?.response?.data?.message ||
                "An error occurred while getting the rule details."
            );
            setAlertType("error");
            setAlertOpen(true);
        }
        finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if(currentRule?.id) getRuleAPI();
    }, [currentRule])

    const handleAdd = (partner: Partner) => {
        setMatchedPartners((prevMatched) =>
            prevMatched.filter((p) => p.id !== partner.id)
        );

        setAddedPartners((prevAdded) => {
            const alreadyAdded = prevAdded.some((p) => p.id === partner.id);
            return alreadyAdded ? prevAdded : [...prevAdded, partner];
        });
    };

    const handleRemove = (partner: Partner) => {
        setAddedPartners((prevAdded) =>
            prevAdded.filter((p) => p.id !== partner.id)
        );

        setMatchedPartners((prevMatched) => {
            const alreadyMatched = prevMatched.some((p) => p.id === partner.id);
            return alreadyMatched ? prevMatched : [...prevMatched, partner];
        });
    };

    const initialValues = {
        ruleName: currentRule?.ruleName || "",
        isActive: currentRule?.isActive || "Active",
    };

    const validationSchema = Yup.object().shape({
        ruleName: Yup.string()
            .required("Rule name is required"),
        isActive: Yup.string().required("Status is required"),

    });

    const newlyAddedPartners = addedPartners.filter(
        (p) => !existingAddedPartners.some((ep) => ep.id === p.id)
    );

    const removedPartners = existingAddedPartners.filter(
        (ep) => !addedPartners.some((p) => p.id === ep.id)
    );

    const editRuleAPI = async (data: any) => {
        const payload = {
            ruleName: data?.ruleName || "New Rule",
            isActive: data?.isActive === "Active" ? true : false,
            newlyAddedPartnerIds: newlyAddedPartners.map((p) => p.id),
            removedPartnerIds: removedPartners.map((p) => p.id),
        };
        if (addedPartners?.length === 0) {
            setAlertMessage("At least one partner must be assigned to the logic, You cannot remove the only assigned partner.")
            setAlertType("warning")
            setAlertOpen(true)
        }
        else {
            setLoading(true);
            try {
                const response = await editRule(currentRule?.id, payload);
                if (response.data.success) {
                    fetchRules();
                    navigate(routes.ASSIGNMENTRULES, {
                        state: {
                            message: "Assignment rule updated successfully.",
                            type: "success",
                            fromEditRule: true,
                        },
                    });
                }
            } catch (error: any) {
                setAlertMessage(
                    error?.response?.data?.message ||
                    "An error occurred while updating the rule."
                );
                setAlertType("error");
                setAlertOpen(true);
            } finally {
                setLoading(false);
            }
        }
        
    };


    const handleSubmit = (values: typeof initialValues) => {
        editRuleAPI(values)
    };

    const hasChanges = (formik: any) => {
        return formik.values.ruleName !== currentRule?.ruleName ||
            formik.values.isActive !== (currentRule?.isActive) ||
            newlyAddedPartners.length > 0 ||
            removedPartners.length > 0;
    }
    const showLoading = useMemo(() => loading, [loading]);
    useSetLoading(showLoading);

    return (
        <div className="edit-rule-wrapper">
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize={true}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <div className="edit-rule-body-wrapper">
                            <div className="rule-criteria-details-wrapper">
                                <div className="left-panel-section">
                                    <LeftPanel viewRule={viewRule} />
                                </div>
                                <div className="criteria-used">
                                    <h2 className="criteria-header">Criteria Used</h2>
                                    <div className="criteria-section">
                                        <label className="criteria-label">
                                            Geography: {currentRule?.geographyParam}
                                        </label>
                                        <div className="criteria-pill">
                                            {currentRule?.geographyValue}
                                        </div>
                                    </div>
                                    {currentRule?.productType?.length && <div className="criteria-section">
                                        <label className="criteria-label">Product</label>
                                        <div className="criteria-tags">
                                            {currentRule?.productType[0] && <div className="criteria-tag">{currentRule?.productType[0]}</div>}
                                            {currentRule?.productType[1] && <div className="criteria-tag">{currentRule?.productType[1]}</div>}
                                        </div>
                                    </div>}
                                  
                                </div>
                            </div>
                            <div className="right-panel">
                                <div className="right-panel-body edit-rule-right-panel-body">
                                    <div className="assign-rule-wrapper">
                                        <ListOfPartnersBasedOnRule
                                            matchedPartners={matchedPartners}
                                            addedPartners={addedPartners}
                                            onAdd={handleAdd}
                                            onRemove={handleRemove}
                                            viewRule={viewRule}
                                        />
                                        {!viewRule && <div className="button-wrapper">
                                            <div className="custom-btn-wrapper">
                                                <button
                                                    type="button"
                                                    className="custom-btn reset-btn"
                                                    onClick={() => {
                                                        formik.resetForm();
                                                        setIsEdit(false)
                                                    }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                            <div className="custom-btn-wrapper">
                                                <button
                                                    type="submit"
                                                    className="custom-btn proceed-btn"
                                                    onClick={() => handleSubmit}
                                                    disabled={!hasChanges(formik)}
                                                >
                                                    Save Changes
                                                </button>
                                            </div>
                                        </div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                )}
            </Formik>
            <AlertComponent
                message={alertMessage}
                type={alertType ?? "info"}
                open={alertOpen}
                onClose={() => setAlertOpen(false)}
            />
        </div>
    );
};

export default EditRule;
